/**
 * Autocomplete Client using autosuggest-sdk
 * 
 * This module wraps the autosuggest-sdk to provide autocomplete functionality
 * for the application. The SDK handles normalization internally.
 */

import { createAutosuggestClient } from '@datadirects/autosuggest-sdk';
import type { NormalizedCarItem } from '@datadirects/autosuggest-sdk';
import type { IAirportItem } from '../components/Common/AirportSelect/Item';

/**
 * Determine environment from NEXT_PUBLIC_SENTRY_ENV or NEXT_PUBLIC_BACKEND_URL
 */
function getEnvironment(): 'acpt' | 'int' | 'prod' {
	// Check SENTRY_ENV first
	const sentryEnv = process.env.NEXT_PUBLIC_SENTRY_ENV?.toLowerCase();
	if (sentryEnv === 'prod' || sentryEnv === 'production') {
		return 'prod';
	}
	if (sentryEnv === 'int' || sentryEnv === 'integration') {
		return 'int';
	}
	if (sentryEnv === 'acpt' || sentryEnv === 'acceptance' || sentryEnv === 'dev') {
		return 'acpt';
	}

	// Fallback: check BACKEND_URL
	const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || '';
	if (backendUrl.includes('.prod.') || backendUrl.includes('travelpricedrops.com')) {
		return 'prod';
	}
	if (backendUrl.includes('.int.')) {
		return 'int';
	}

	// Default to acpt for development
	return 'acpt';
}

// Create the autosuggest client with configuration
// Environment is determined automatically from NEXT_PUBLIC_SENTRY_ENV:
// - prod → production API
// - int → integration API
// - acpt/dev → acceptance API (default)
const client = createAutosuggestClient({
	mode: 'new',
	env: getEnvironment(),
	timeoutMs: 5000,
	debounceMs: 300,
});

/** Debounce / superseded requests — not worth logging as errors. */
function isExpectedAutosuggestCancellation(error: unknown): boolean {
	if (error == null || typeof error !== 'object') {
		return false;
	}
	const e = error as { name?: string; message?: string };
	if (e.name === 'AbortError') {
		return true;
	}
	const msg = typeof e.message === 'string' ? e.message : '';
	return /debounced/i.test(msg);
}

/**
 * Fetch autocomplete suggestions for flights
 * 
 * @param query - The search query
 * @param locale - Optional locale code (e.g., 'en-US', 'de-DE')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of airport/city items in legacy format
 */
export async function fetchFlightAutocomplete(
	query: string,
	locale?: string,
	signal?: AbortSignal
): Promise<IAirportItem[]> {
	try {
		const response = await client.fetch({
			query,
			locale: locale || 'en-US',
			vertical: 'flights',
			normalizeResponse: true,
			signal,
		});

		// SDK returns normalized data in legacy format
		if (!response.data || !Array.isArray(response.data)) {
			return [];
		}

		// SDK already provides data in the correct format
		return response.data as IAirportItem[];
	} catch (error) {
		if (!isExpectedAutosuggestCancellation(error)) {
			console.error('Autocomplete SDK error:', error);
		}
		return [];
	}
}

/**
 * Map raw primaryPlaceType to kayakType used by hotelActions.
 * - city/town → 'city'
 * - hotel → 'hotel'
 * - airport → 'ap'
 */
function mapKayakType(primaryPlaceType: string): string {
	switch (primaryPlaceType) {
		case 'city':
		case 'town':
			return 'city';
		case 'hotel':
			return 'hotel';
		case 'airport':
			return 'ap';
		default:
			return 'city';
	}
}

/**
 * Build ptid in the format expected by hotelActions.
 * hotelActions does: 'C:' + destination.ptid.substr(1) for cities
 *                    'H:' + destination.ptid.substr(1) for hotels
 * So we need:
 * - cities:  "c<cityId>"  → substr(1) yields "<cityId>"  → "C:<cityId>"
 * - hotels:  "h<hotelId>" → substr(1) yields "<hotelId>" → "H:<hotelId>"
 */
function buildPtid(primaryPlaceType: string, cityId?: number, hotelId?: number): string {
	switch (primaryPlaceType) {
		case 'city':
		case 'town':
			return cityId != null ? `c${cityId}` : '';
		case 'hotel':
			return hotelId != null ? `h${hotelId}` : '';
		default:
			return '';
	}
}

/**
 * Fetch autocomplete suggestions for hotels
 *
 * @param query - The search query
 * @param locale - Optional locale code (e.g., 'en-US', 'de-DE')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of hotel/city items in IAirportItem format
 */
export async function fetchHotelAutocomplete(
	query: string,
	locale?: string,
	signal?: AbortSignal
): Promise<IAirportItem[]> {
	try {
		const response = await client.fetch({
			query,
			locale: locale || 'en-US',
			vertical: 'hotels',
			normalizeResponse: false,
			signal,
		});

		// Raw response: { source: 'new', vertical, data: { results: [...] } }
		const data = response.data as any;
		const results = data?.results;
		if (!results || !Array.isArray(results)) {
			return [];
		}

		// Map raw autosuggest API items to IAirportItem format
		const mappedResults = results.map((item: any) => {
			const primaryPlaceType = item.primaryPlaceType || 'city';
			const kayakType = mapKayakType(primaryPlaceType);

			let type = 'city';
			if (kayakType === 'ap') {
				type = 'airport';
			} else if (kayakType === 'hotel') {
				type = 'hotel';
			}

			const ptid = buildPtid(primaryPlaceType, item.cityId, item.hotelId);

			return {
				name: item.name || item.cityName || '',
				country: item.countryName || '',
				city: item.cityName || '',
				state: item.regionName || '',
				iata: item.iataCode || '',
				type,
				documentId: item.entityKey || `${primaryPlaceType}_${item.placeId}` || '',
				cityID: item.cityId != null ? String(item.cityId) : '',
				airportID: '',
				allAirports: false,
				kayakType,
				ptid,
				countryCode: item.countryCode || '',
				stateCode: item.regionName || '',
			} as IAirportItem;
		});

		return mappedResults;
	} catch (error) {
		if (!isExpectedAutosuggestCancellation(error)) {
			console.error('Autocomplete SDK error:', error);
		}
		return [];
	}
}

/** Match legacy Kayak cars shape (`C:<id>`); SDK normalizer uses `c:<id>`. */
function normalizeCarCityIdForLegacy(cityID: string | undefined): string | undefined {
	if (!cityID) {
		return undefined;
	}
	const m = /^c:(.+)$/i.exec(cityID);
	if (m) {
		return `C:${m[1]}`;
	}
	return cityID;
}

function mapNormalizedCarItemToAirportItem(item: NormalizedCarItem): IAirportItem {
	const isAirport = item.type === 'airport' || item.loctype === 'ap';
	if (isAirport) {
		return {
			country: item.country,
			name: item.name,
			city: item.cityonly || item.city,
			state: item.state,
			iata: item.iata,
			type: 'airport',
			stateCode: item.stateCode || item.rc || '',
			countryCode: item.countryCode || item.cc || '',
			documentId: item.documentId,
			airportID: item.airportID,
			kayakType: 'ap',
		};
	}
	return {
		country: item.country,
		name: item.name,
		city: item.cityonly || item.city,
		state: item.state,
		type: 'city',
		cityID: normalizeCarCityIdForLegacy(item.cityID),
		stateCode: item.stateCode || item.rc || '',
		countryCode: item.countryCode || item.cc || '',
		documentId: item.documentId,
		kayakType: 'city',
	};
}

/**
 * Fetch autocomplete for cars (cities + airports), normalized to IAirportItem.
 */
export async function fetchCarAutocomplete(
	query: string,
	locale?: string,
	signal?: AbortSignal
): Promise<IAirportItem[]> {
	try {
		const response = await client.fetch({
			query,
			locale: locale || 'en-US',
			vertical: 'cars',
			normalizeResponse: true,
			signal,
		});

		if (!response.data || !Array.isArray(response.data)) {
			return [];
		}

		return (response.data as NormalizedCarItem[]).map(mapNormalizedCarItemToAirportItem);
	} catch (error) {
		if (!isExpectedAutosuggestCancellation(error)) {
			console.error('Autocomplete SDK error (cars):', error);
		}
		return [];
	}
}

export default {
	fetchFlightAutocomplete,
	fetchHotelAutocomplete,
	fetchCarAutocomplete,
};
